import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Trophy, ArrowRight, ArrowLeft, Check, Plus, X, Sparkles, Mail, Users, Coins, Award, Play, Lightbulb, Zap } from "lucide-react";
import LogoUpload from "../components/LogoUpload";

/**
 * Wizard Crea Lega — 4 step con Ricette pre-compilate + Tutorial video (iter 23)
 * 1. Identità (con Preset "Ricette veloci")
 * 2. Regolamento
 * 3. Invita
 * 4. Riepilogo
 */

const ROLE_SYSTEMS = [
  { id: "mantra",  emoji: "🎯", name: "Mantra",   desc: "Ruoli specialistici (Por, Dc, Dd, B, E, M, C, W, T, A, Pc). L'unico sistema supportato — pensato per campionati manageriali reali come Lega dei Magnati." },
];

const STEPS = [
  { n: 1, label: "Identità", emoji: "🎨" },
  { n: 2, label: "Regolamento", emoji: "⚙️" },
  { n: 3, label: "Invita", emoji: "📩" },
  { n: 4, label: "Riepilogo", emoji: "✅" },
];

/**
 * Preset pack "Ricette veloci" — riempie tutti i campi in un click.
 * L'utente può poi personalizzare qualunque valore prima di confermare.
 */
const PRESETS = [
  {
    id: "mantra-10",
    emoji: "🎯",
    name: "Mantra 10",
    tagline: "La ricetta più amata",
    desc: "10 squadre, sistema Mantra con ruoli specialistici, 500 crediti a testa. Perfetta per la tua prima lega o gruppi affiatati.",
    apply: { roleSystem: "mantra", budget: 500, numTeams: 10, sampleName: "Champions Club" },
  },
  {
    id: "mantra-12",
    emoji: "🏆",
    name: "Mantra Pro 12",
    tagline: "Per chi vuole tattica pura",
    desc: "12 squadre, sistema Mantra full con moduli avanzati, 700 crediti. Setup competitivo come Lega dei Magnati.",
    apply: { roleSystem: "mantra", budget: 700, numTeams: 12, sampleName: "Fantavvocati F.C." },
  },
  {
    id: "mantra-8",
    emoji: "⚡",
    name: "Mantra 8",
    tagline: "Snella e veloce",
    desc: "8 squadre, sistema Mantra, 400 crediti. Ideale per una lega tra colleghi o cerchia piccola.",
    apply: { roleSystem: "mantra", budget: 400, numTeams: 8, sampleName: "Lega Ufficio" },
  },
  {
    id: "custom",
    emoji: "🎨",
    name: "Personalizzata",
    tagline: "Fai da te",
    desc: "Parti da zero e configura ogni parametro come preferisci. Nessun valore pre-compilato.",
    apply: null, // no-op
  },
];

const TUTORIAL_URL = process.env.REACT_APP_TUTORIAL_VIDEO_URL || "";

export default function CreateLeague() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [logoId, setLogoId] = useState(null);
  const [roleSystem, setRoleSystem] = useState("mantra");
  const [budget, setBudget] = useState(500);
  const [numTeams, setNumTeams] = useState(10);
  const [inviteEmails, setInviteEmails] = useState([]);
  const [inviteInput, setInviteInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const nav = useNavigate();

  const applyPreset = (preset) => {
    setSelectedPreset(preset.id);
    if (!preset.apply) return; // "Personalizzata" leaves everything untouched
    setRoleSystem(preset.apply.roleSystem);
    setBudget(preset.apply.budget);
    setNumTeams(preset.apply.numTeams);
    // Only pre-fill the name if it's still empty — never overwrite user input.
    if (!name.trim()) setName(preset.apply.sampleName);
    toast.success(`🎨 Preset "${preset.name}" applicato`);
  };

  useEffect(() => {
    api.get("/pricing/league").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const canGoNext = useMemo(() => {
    if (step === 1) return name.trim().length >= 2;
    if (step === 2) return Number(budget) >= 100 && Number(numTeams) >= 6 && Number(numTeams) <= 14;
    return true;
  }, [step, name, budget, numTeams]);

  const addEmail = () => {
    const e = inviteInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Email non valida"); return; }
    if (inviteEmails.includes(e)) { toast.error("Email già in lista"); return; }
    setInviteEmails([...inviteEmails, e]);
    setInviteInput("");
  };
  const removeEmail = (e) => setInviteEmails(inviteEmails.filter(x => x !== e));

  async function submit() {
    setLoading(true);
    try {
      const settings = { initial_budget: Number(budget), num_teams: Number(numTeams), role_system: roleSystem };
      const { data } = await api.post("/leagues", {
        name: name.trim(),
        logo_url: logoId || null,
        settings,
        template: "manageriale",
        pending_invites: inviteEmails,
      });
      toast.success(`Lega "${data.name}" creata! Ora completa il pagamento.`);
      nav(`/pagamento/${data.league_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-white antialiased">
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b1120] via-[#0d1428] to-[#0b1120]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-emerald-500/[0.05] rounded-full blur-[120px]" />
      </div>

      {/* NAV */}
      <nav className="border-b border-white/[0.06] backdrop-blur-xl bg-[#0b1120]/80 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-dashboard">
            <ChevronLeft size={18} /> Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setTutorialOpen(true)}
              data-testid="open-tutorial"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-xs font-bold transition-colors">
              <Play size={12} className="fill-emerald-300"/> Guarda il tutorial
            </button>
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/brand/logo-256.png"
                alt="MyFantabusiness" className="w-10 h-10 object-contain"/>
              <span className="font-black text-[15px]">My<span className="text-emerald-400">Fantabusiness</span></span>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        {/* Stepper */}
        <div className="mb-8" data-testid="wizard-stepper">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => {
              const done = s.n < step;
              const active = s.n === step;
              return (
                <div key={s.n} className="flex-1 flex items-center gap-1.5">
                  <div className={`flex-1 flex items-center gap-2 rounded-xl px-3 py-2 border transition-all ${
                    active ? "border-emerald-500/60 bg-emerald-500/10 text-white"
                    : done ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                    : "border-white/[0.08] bg-white/[0.02] text-slate-500"
                  }`} data-testid={`step-${s.n}${active ? "-active" : done ? "-done" : ""}`}>
                    <span className="text-base">{done ? "✓" : s.emoji}</span>
                    <div className="min-w-0 hidden sm:block">
                      <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">Step {s.n}</div>
                      <div className="text-xs font-bold truncate">{s.label}</div>
                    </div>
                  </div>
                  {i < STEPS.length - 1 && <div className={`w-2 h-0.5 ${done ? "bg-emerald-500" : "bg-white/10"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-panel">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1">Step 1 · Identità</div>
                <h2 className="text-2xl font-black">Come si chiama la tua lega?</h2>
                <p className="text-sm text-slate-400 mt-1">Nome e logo saranno visibili a tutti i manager.</p>
              </div>

              {/* Help ticker */}
              <div className="flex items-start gap-2 rounded-xl border border-sky-400/30 bg-sky-500/[0.06] px-3 py-2.5 text-xs text-sky-200" data-testid="help-ticker-1">
                <Lightbulb size={14} className="text-sky-300 shrink-0 mt-0.5"/>
                <div>
                  <span className="font-bold text-sky-100">Suggerimento:</span> non sai da dove partire? Scegli una <span className="font-bold">Ricetta veloce</span> qui sotto — pre-compiliamo regolamento e crediti, tu personalizzi solo quello che vuoi. Puoi anche{" "}
                  <button type="button" onClick={() => setTutorialOpen(true)}
                    data-testid="help-ticker-tutorial-link"
                    className="underline underline-offset-2 hover:text-sky-100 font-bold">guardare il tutorial</button>.
                </div>
              </div>

              {/* Preset "Ricette veloci" */}
              <div>
                <label className="text-xs tracking-widest text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-3">
                  <Zap size={12} className="text-amber-400"/> Ricette veloci
                </label>
                <div className="grid sm:grid-cols-2 gap-3" data-testid="presets-grid">
                  {PRESETS.map(p => {
                    const active = selectedPreset === p.id;
                    return (
                      <button key={p.id} type="button" onClick={() => applyPreset(p)}
                        data-testid={`preset-${p.id}`}
                        className={`text-left p-4 rounded-xl border transition-all group ${
                          active
                            ? "border-emerald-500 bg-emerald-500/10 shadow-[0_4px_20px_rgba(16,185,129,0.15)]"
                            : "border-white/10 bg-white/[0.02] hover:border-emerald-500/40 hover:bg-white/[0.04]"
                        }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xl">{p.emoji}</span>
                          <span className="font-black text-white">{p.name}</span>
                          {active && <Check size={14} className="text-emerald-400 ml-auto" strokeWidth={3}/>}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/80 mb-1.5">{p.tagline}</div>
                        <div className="text-xs text-slate-400 leading-relaxed">{p.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs tracking-widest text-slate-400 uppercase font-bold block mb-2">Nome lega</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && canGoNext && setStep(2)}
                  autoFocus placeholder="Champions Club 26"
                  data-testid="wizard-name"
                  className="w-full bg-[#0b1120] border border-white/10 rounded-lg px-4 py-3 text-lg focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"/>
                <div className="text-[10px] text-slate-500 mt-1">Minimo 2 caratteri</div>
              </div>
              <div>
                <label className="text-xs tracking-widest text-slate-400 uppercase font-bold block mb-2">Logo (opzionale)</label>
                <LogoUpload value={logoId} onChange={setLogoId} scope="league" size="lg" testid="wizard-logo"/>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-2-panel">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1">Step 2 · Regolamento</div>
                <h2 className="text-2xl font-black">Il campionato è sempre manageriale.</h2>
                <p className="text-sm text-slate-400 mt-1">Rose permanenti, contratti, bilanci. Configura crediti d&apos;asta e numero partecipanti — il sistema ruoli è <span className="text-emerald-300 font-bold">Mantra</span>, l&apos;unico supportato.</p>
              </div>

              {/* Sistema ruoli (info-only, Mantra è l'unico supportato) */}
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 flex items-start gap-3" data-testid="role-system-info">
                <span className="text-2xl">🎯</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-black text-white">Sistema Mantra</span>
                    <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-300 bg-emerald-500/20 px-1.5 py-0.5 rounded">In uso</span>
                  </div>
                  <div className="text-xs text-slate-400 leading-relaxed">Ruoli specialistici (Por, Dc, Dd, B, E, M, C, W, T, A, Pc) con moduli tattici avanzati. L&apos;unico sistema supportato — pensato per campionati manageriali reali come Lega dei Magnati.</div>
                </div>
              </div>

              {/* Crediti */}
              <div>
                <label className="text-xs tracking-widest text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-2">
                  <Coins size={12} className="text-amber-400"/> Crediti asta iniziale (per squadra)
                </label>
                <div className="flex items-center gap-3">
                  <input type="range" min="100" max="3000" step="50" value={budget}
                    onChange={e => setBudget(e.target.value)} data-testid="wizard-budget-range"
                    className="flex-1 accent-emerald-500 h-2"/>
                  <input type="number" min="100" max="3000" step="50" value={budget}
                    onChange={e => setBudget(e.target.value)} data-testid="wizard-budget"
                    className="w-24 bg-[#0b1120] border border-white/10 rounded-lg px-3 py-2 text-center font-mono font-bold focus:border-emerald-500 focus:outline-none"/>
                  <span className="text-sm text-slate-400 font-mono">cr</span>
                </div>
              </div>

              {/* Partecipanti */}
              <div>
                <label className="text-xs tracking-widest text-slate-400 uppercase font-bold flex items-center gap-1.5 mb-2">
                  <Users size={12} className="text-sky-400"/> Partecipanti
                </label>
                <div className="flex items-center gap-3">
                  <input type="range" min="6" max="14" step="1" value={numTeams}
                    onChange={e => setNumTeams(e.target.value)} data-testid="wizard-num-teams-range"
                    className="flex-1 accent-emerald-500 h-2"/>
                  <input type="number" min="6" max="14" step="1" value={numTeams}
                    onChange={e => setNumTeams(e.target.value)} data-testid="wizard-num-teams"
                    className="w-24 bg-[#0b1120] border border-white/10 rounded-lg px-3 py-2 text-center font-mono font-bold focus:border-emerald-500 focus:outline-none"/>
                  <span className="text-sm text-slate-400 font-mono">squadre</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Da 6 a 14 partecipanti.</div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3-panel">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1">Step 3 · Invita</div>
                <h2 className="text-2xl font-black">Invita i manager. <span className="text-slate-500 font-bold text-lg">(opzionale)</span></h2>
                <p className="text-sm text-slate-400 mt-1">Salviamo le email — dopo la creazione riceverai un codice invito da condividere. Le email verranno usate per invii automatici in futuro.</p>
              </div>
              <div className="flex gap-2">
                <input type="email" value={inviteInput} onChange={e => setInviteInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addEmail())}
                  placeholder="mario.rossi@email.it" data-testid="wizard-invite-input"
                  className="flex-1 bg-[#0b1120] border border-white/10 rounded-lg px-3 py-2.5 focus:border-emerald-500 focus:outline-none"/>
                <button type="button" onClick={addEmail} disabled={!inviteInput.trim()}
                  data-testid="wizard-invite-add"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 font-bold text-sm disabled:opacity-40">
                  <Plus size={14}/> Aggiungi
                </button>
              </div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="wizard-invite-list">
                {inviteEmails.length === 0 && (
                  <div className="text-center text-xs text-slate-500 italic py-6 border border-dashed border-white/10 rounded-lg">
                    Nessuna email aggiunta. Puoi saltare e invitare in seguito con il codice lega.
                  </div>
                )}
                {inviteEmails.map((e, i) => (
                  <div key={e} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08]" data-testid={`invite-row-${i}`}>
                    <Mail size={14} className="text-slate-500"/>
                    <span className="flex-1 text-sm text-slate-200 truncate">{e}</span>
                    <button type="button" onClick={() => removeEmail(e)} data-testid={`invite-remove-${i}`}
                      className="p-1 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-300 transition-colors">
                      <X size={12}/>
                    </button>
                  </div>
                ))}
              </div>
              {inviteEmails.length > 0 && (
                <div className="text-[11px] text-slate-500">
                  {inviteEmails.length} email in lista. Le comunicherai al momento via WhatsApp o attenderai la nostra integrazione email.
                </div>
              )}
            </div>
          )}

          {/* STEP 4 — Riepilogo */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-4-panel">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-1">Step 4 · Riepilogo</div>
                <h2 className="text-2xl font-black">Tutto pronto per il calcio d&apos;inizio.</h2>
                <p className="text-sm text-slate-400 mt-1">Rivedi e conferma. Potrai comunque cambiare quasi tutto dopo.</p>
              </div>
              <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-5 space-y-3">
                <RecapRow label="Nome lega" value={name} icon={<Sparkles size={13} className="text-emerald-400"/>}/>
                <RecapRow label="Tipo campionato" value="💼 Manageriale" icon={<Award size={13} className="text-emerald-400"/>}/>
                <RecapRow label="Sistema ruoli" value={`${ROLE_SYSTEMS.find(r=>r.id===roleSystem)?.emoji} ${ROLE_SYSTEMS.find(r=>r.id===roleSystem)?.name}`} icon={<Award size={13} className="text-purple-400"/>}/>
                <RecapRow label="Crediti asta" value={`${budget} cr`} icon={<Coins size={13} className="text-amber-400"/>}/>
                <RecapRow label="Partecipanti" value={`${numTeams} squadre`} icon={<Users size={13} className="text-sky-400"/>}/>
                <RecapRow label="Manager invitati" value={inviteEmails.length ? `${inviteEmails.length} email in lista` : "Nessuno (condividi il codice)"} icon={<Mail size={13} className="text-purple-400"/>}/>
              </div>
              <div className="rounded-xl border border-amber-400/30 bg-amber-400/[0.05] p-4">
                <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300 mb-1">💰 Pagamento richiesto</div>
                {pricing ? (
                  <div className="text-sm text-slate-200 space-y-1">
                    {pricing.is_discount ? (
                      <>
                        <div>
                          Al termine del wizard verrai portato al pagamento sicuro di{" "}
                          <span className="font-bold text-amber-300">{pricing.amount_eur} €</span>{" "}
                          <span className="text-slate-400 text-xs">
                            (invece di <span className="line-through">{pricing.standard_eur} €</span>)
                          </span>{" "}
                          per attivare la lega per l&apos;intera stagione.
                        </div>
                        <div className="text-[11px] text-amber-300 font-bold">
                          ✨ {pricing.reason}
                        </div>
                      </>
                    ) : (
                      <div>
                        Al termine del wizard verrai portato al pagamento sicuro di{" "}
                        <span className="font-bold text-white">{pricing.amount_eur} €</span>{" "}
                        per attivare la lega per l&apos;intera stagione. Tutti i {numTeams} partecipanti potranno iniziare subito.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-400">Caricamento prezzo…</div>
                )}
              </div>
            </div>
          )}

          {/* NAVIGATION */}
          <div className="mt-8 flex gap-3">
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)} disabled={loading}
                data-testid="wizard-back"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 hover:border-white/25 text-slate-300 font-bold text-sm disabled:opacity-40">
                <ArrowLeft size={14}/> Indietro
              </button>
            )}
            {step < 4 && (
              <button type="button" onClick={() => canGoNext && setStep(step + 1)}
                disabled={!canGoNext}
                data-testid="wizard-next"
                className="flex-1 sm:flex-none sm:ml-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black text-sm disabled:opacity-40 shadow-[0_4px_18px_rgba(16,185,129,0.35)]">
                Prossimo <ArrowRight size={14}/>
              </button>
            )}
            {step === 4 && (
              <button type="button" onClick={submit} disabled={loading}
                data-testid="wizard-submit"
                className="flex-1 sm:flex-none sm:ml-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0b1120] font-black disabled:opacity-40 shadow-[0_8px_24px_rgba(16,185,129,0.4)]">
                {loading ? "Creazione…" : (<>Vai al pagamento – {pricing?.amount_eur ?? 69} € <ArrowRight size={16}/></>)}
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Tutorial Video Modal */}
      {tutorialOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          data-testid="tutorial-modal"
          onClick={() => setTutorialOpen(false)}>
          <div onClick={e => e.stopPropagation()}
            className="relative w-full max-w-3xl rounded-2xl border border-emerald-500/30 bg-[#0d1428] shadow-2xl shadow-emerald-500/10 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Play size={16} className="text-emerald-400 fill-emerald-400"/>
                <span className="font-black uppercase tracking-wider text-sm">Tutorial · Come creare la tua lega</span>
              </div>
              <button type="button" onClick={() => setTutorialOpen(false)}
                data-testid="tutorial-close"
                className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                <X size={16}/>
              </button>
            </div>
            <div className="aspect-video bg-black">
              {TUTORIAL_URL ? (
                <iframe
                  src={TUTORIAL_URL}
                  title="Tutorial MyFantabusiness"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="tutorial-iframe"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-6 text-center bg-gradient-to-br from-slate-900 to-black" data-testid="tutorial-placeholder">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center border-2 border-emerald-500/40">
                    <Play size={28} className="text-emerald-400 fill-emerald-400 ml-1"/>
                  </div>
                  <div className="text-white font-black text-lg">Video tutorial in arrivo!</div>
                  <p className="text-sm text-slate-400 max-w-md">
                    Nel frattempo, ecco i 3 punti chiave: <span className="text-emerald-300 font-bold">1)</span> scegli una Ricetta veloce per pre-compilare tutto in un click, <span className="text-emerald-300 font-bold">2)</span> personalizza nome, budget e partecipanti, <span className="text-emerald-300 font-bold">3)</span> invita i manager con il codice lega che riceverai dopo l&apos;attivazione.
                  </p>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-2">
                    Setup admin: imposta REACT_APP_TUTORIAL_VIDEO_URL per il video embedded
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-500">
              <div>Domande? Scrivi a <a href="mailto:supporto@myfantabusiness.it" className="text-emerald-400 hover:underline">supporto@myfantabusiness.it</a></div>
              <button type="button" onClick={() => setTutorialOpen(false)}
                data-testid="tutorial-close-footer"
                className="text-slate-400 hover:text-white font-bold">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecapRow({ label, value, icon }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-6 flex items-center justify-center">{icon}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 w-32 shrink-0">{label}</div>
      <div className="flex-1 text-white font-semibold truncate">{value || <span className="text-slate-600 italic">—</span>}</div>
    </div>
  );
}
